
import React, { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
// Material Dashboard 2 React components
import MDBox from "../controls/MDBox";
import MDTypography from "../controls/MDTypography";
import { getData } from "../Utils/Http/api";

// Material Dashboard 2 React example components
import SidenavCollapse from "../Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "../Sidenav/SidenavRoot";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "../../context";
import { CloseButton } from "react-bootstrap";

function Sidenav({ color, brand, brandIcon, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor  } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");
  const [serviceStatus, setServiceStatus] = useState(null);
  const [serviceInfo, setServiceInfo] = useState({ version: "", environment: "" });

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Check service status
  const checkServiceStatus = async () => {
    try {
      const response = await getData("/status");
      setServiceStatus(response?.estado === "Ready" ? "ready" : "error");
      setServiceInfo({
        version: response?.version || "",
        environment: response?.environment || ""
      });
    } catch (error) {
      console.error("Error checking service status:", error);
      setServiceStatus("error");
      setServiceInfo({ version: "", environment: "" });
    }
  };

  useEffect(() => {
    // Initial check when component mounts
    checkServiceStatus();
  }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location, transparentSidenav, whiteSidenav]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route, children }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
       
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
             
            />
           

          </Link>

      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    } else if (type === "title") {
      const text = title || name || "";
      returnValue = (
        <MDTypography
          key={key || `title-${text}`}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {text}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
    {...rest}
    variant="permanent"
    ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode, sidenavColor }}
    sx={{
      width: miniSidenav ? "80px" : "280px", // Ancho dinámico basado en miniSidenav
      transition: "width 0.3s ease",
    }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          
          <MDTypography variant="h6" color="secondary">
            <CloseButton/>
            
          </MDTypography>
        </MDBox>
        {/* Logo sobre fondo blanco para que se lea con cualquier color de sidenav */}
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          bgColor="white"
          borderRadius="lg"
          py={1}
          px={miniSidenav ? 0.5 : 1.5}
          mx={miniSidenav ? -2.5 : 0}
        >
          <MDBox
            component="img"
            src={miniSidenav && brandIcon ? brandIcon : brand}
            alt={brandName}
            sx={{ height: "2.25rem", maxWidth: "100%" }}
          />
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <MDBox 
        sx={{ 
          height: 'calc(100vh - 180px)', 
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        <List>{renderRoutes}</List>
      </MDBox>
      
      {/* Service Status Indicator at Bottom */}
      <MDBox 
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        px={2}
        py={1}
        sx={{
          pointerEvents: 'auto',
          zIndex: 1000
        }}
      >
        <MDBox 
          display="flex" 
          flexDirection="column"
          gap={0.5}
          py={0.5}
          px={1}
          sx={{
            backgroundColor: serviceStatus === "ready" 
              ? "rgba(76, 175, 80, 0.15)" 
              : serviceStatus === "error" 
              ? "rgba(244, 67, 54, 0.15)" 
              : "rgba(158, 158, 158, 0.15)",
            borderRadius: 1,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: serviceStatus === "ready" 
                ? "rgba(76, 175, 80, 0.25)" 
                : serviceStatus === "error" 
                ? "rgba(244, 67, 54, 0.25)" 
                : "rgba(158, 158, 158, 0.25)",
            }
          }}
          onClick={checkServiceStatus}
        >
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            {serviceStatus === "ready" ? (
              <CheckCircleIcon sx={{ color: "success.main", fontSize: 16 }} />
            ) : serviceStatus === "error" ? (
              <ErrorIcon sx={{ color: "error.main", fontSize: 16 }} />
            ) : null}
            {!miniSidenav && (
              <MDTypography variant="caption" color={textColor} fontWeight="medium" fontSize="0.7rem">
                Estado: {serviceStatus === "ready" ? "OK" : serviceStatus === "error" ? "Error" : "..."}
              </MDTypography>
            )}
          </MDBox>
          
          {!miniSidenav && (serviceInfo.version || serviceInfo.environment) && (
            <MDBox display="flex" flexDirection="column" alignItems="center" gap={0.2}>
              {serviceInfo.version && (
                <MDTypography variant="caption" color={textColor} fontSize="0.65rem">
                  v{serviceInfo.version} - {serviceInfo.environment}
                </MDTypography>
              )}
              
            </MDBox>
          )}
        </MDBox>
      </MDBox>

    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
  brandIcon: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandIcon: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
